import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { shareLinkApi, PublicShareLinkData} from '../../services/shareLinkApi';
import { AlertCircle, RefreshCw, ArrowLeft, ArrowRight } from 'lucide-react';
import HelperFunctions from '../../utils/helperFunctions';
import '../../styles/pages/public-share-view.css';
import { getUserTimezone } from '../../utils/timezoneUtils';

const PublicShareView: React.FC = () => {
  const { url } = useParams<{ url: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<PublicShareLinkData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 860);
  const userPreferences = JSON.parse(localStorage.getItem('userPreferences') || '{}');
  const timezone = userPreferences?.timezone || getUserTimezone();

  const fetchData = useCallback(async () => {
    if (!url) {
      navigate('/', { replace: true });
      return;
    }

    // Validate URL format (should be 16 alphanumeric characters like 4QM1FBTEREK8RUOO).
    // Anything else is a random/invalid path — send the user home rather than show a dead-end error.
    const isValidShareLinkFormat = /^[A-Z0-9]{16}$/.test(url);
    if (!isValidShareLinkFormat) {
      console.warn('Invalid share link format detected, redirecting home:', url);
      navigate('/', { replace: true });
      return;
    }

    try {
      setError(null);
      console.log('Fetching share link data for URL:', url);
      const shareData = await shareLinkApi.getPublicShareLinkData(url);
      console.log('Share link data received:', shareData);
      setData(shareData);
      setIsInitialLoad(false); // Mark initial load as complete after first successful fetch
    } catch (err: any) {
      console.error('Error fetching share link data:', err);
      const message = (err?.message || '').toLowerCase();
      // Treat "not found / expired / invalid / unauthorized" as a dead share link → redirect home.
      // Transient errors (network, timeout, 5xx) fall through to the retry UI below.
      const isDeadShareLink =
        err?.response?.status === 404 ||
        err?.response?.status === 410 ||
        err?.response?.status === 401 ||
        /not\s*found|expired|invalid|revoked|unauthori[sz]ed/.test(message);
      if (isDeadShareLink) {
        navigate('/', { replace: true });
        return;
      }
      setError(err.message || 'Failed to load share link data');
    } finally {
      setLoading(false);
    }
  }, [url, navigate]);

  // Mobile detection
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 860);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  useEffect(() => {
    console.log('PublicShareView mounted or url changed:', url);
    fetchData();
    
    // Auto-refresh every  5 minutes
    const interval = setInterval(() => {
      fetchData();
    }, 300000);

    return () => {
      console.log('Cleaning up interval');
      clearInterval(interval);
    };
  }, [url, fetchData]);

  const handleManualRefresh = () => {
    fetchData();
  };

  const getAQILevel = (subIndex?: number): string => {
    if (!subIndex) return 'N/A';
    if (subIndex <= 50) return 'Good';
    if (subIndex <= 100) return 'Moderate';
    if (subIndex <= 150) return 'USG';
    if (subIndex <= 200) return 'Unhealthy';
    if (subIndex <= 300) return 'Very Unhealthy';
    return 'Hazardous';
  };

  const getAQILevelDescription = (subIndex?: number): string | null => {
    if (!subIndex) return null;
    if (subIndex > 100 && subIndex <= 150) return 'USG - Unhealthy for Sensitive Groups';
    return null;
  };

  const getAQILabel = (aqiStandard: number): string => {
    switch (aqiStandard) {
      case 1:
        return 'AQI-US';
      case 2:
        return 'AQI-UAE';
      case 3:
        return 'AQI-IND';
      default:
        return 'AQI-US';
    }
  };

    // Get Parameter Color  
    const GetParameterColor = (
      value: number | undefined,
      subIndex: number | undefined,
      parameterName: string
    ) => {
      if (
        parameterName === 'PM2.5' ||
        parameterName === 'PM10' ||
        parameterName === 'O₃' ||
        parameterName === 'CO' ||
        parameterName === 'NO₂'
      ) {
        if (subIndex === null) {
          return HelperFunctions.GetParameterColor(value, parameterName);
        } else {
          return HelperFunctions.GetParameterColor(subIndex, 'AQI');
        }
      } else {
        return HelperFunctions.GetParameterColor(value, parameterName);
      }
    };

  const handleDeviceCardClick = (deviceId: number) => {
    // Allow clicking on mobile for 2+ devices, or on desktop for >2 devices
    if (data && ((isMobile && data.devices.length >= 2) || (!isMobile && data.devices.length > 2))) {
      setSelectedDeviceId(deviceId);
    }
  };

  const handleBackClick = () => {
    setSelectedDeviceId(null);
  };


  if (loading) {
    return (
      <div className="public-share-container">
        <div className="loading-spinner">
          <RefreshCw className="spin-icon" size={48} />
          <p>Loading live data...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="public-share-container">
        <div className="error-container">
          <AlertCircle size={48} color="#ef4444" />
          <h2>Failed to Load Share Link</h2>
          <p>{error || 'Share link not found or has expired'}</p>
          <button onClick={handleManualRefresh} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Check if there are no devices
  if (data && (!data.devices || data.devices.length === 0)) {
    return (
      <div className="public-share-container">
        <header className="share-header">
          <div className="header-logo">
            <img 
              src={`${process.env.PUBLIC_URL}/ISTLogo/ISTLOGO.gif`} 
              alt="IST Smart Solutions" 
              className="logo-image"
            />
          </div>
          <div className="header-smarteco">
            <img 
              src={`${process.env.PUBLIC_URL}/SVGIcons/SmartEcoLogo.svg`} 
              alt="SmartEco" 
              className="header-smarteco-logo"
            />
            <p className="header-smarteco-text">app.smarteco.ai</p>
          </div>
        </header>
        <div className="error-container">
          <AlertCircle size={48} color="#6b7280" />
          <h2>No Data Available</h2>
          <p>There is no data available for this share link at the moment.</p>
          <button onClick={handleManualRefresh} className="retry-button">
            Refresh
          </button>
        </div>
      </div>
    );
  }

  // Determine which devices to display
  // On mobile: allow detail view for 2+ devices
  // On desktop: only allow detail view for >2 devices (keep 2-device view unchanged)
  const shouldShowDetailView = isMobile 
    ? (data && data.devices.length >= 2)
    : (data && data.devices.length > 2);
  
  const devicesToDisplay = selectedDeviceId && shouldShowDetailView
    ? data.devices.filter(device => device.deviceId === selectedDeviceId)
    : data?.devices || [];

  // Determine if we're in single device detail view from multi-device view
  const isDetailView = selectedDeviceId !== null && shouldShowDetailView;

  return (
    <div className="public-share-container">
      {/* Header Section */}
      <header className="share-header">
        <div className="header-logo">
          {isDetailView && (
            <button 
              onClick={handleBackClick}
              className="sharelink-back-button"
              aria-label="Back to devices"
            >
              <ArrowLeft size={24} />
            </button>
          )}
          <img 
            src={`${process.env.PUBLIC_URL}/ISTLogo/ISTLOGO.gif`} 
            alt="IST Smart Solutions" 
            className="logo-image"
          />
        </div>
        <div className="header-smarteco">
          <img 
            src={`${process.env.PUBLIC_URL}/SVGIcons/SmartEcoLogo.svg`} 
            alt="SmartEco" 
            className="header-smarteco-logo"
          />
          <p className="header-smarteco-text">app.smarteco.ai</p>
        </div>
      </header>

      {/* Main Content */}
      <div 
        className={`share-content ${
          isDetailView
            ? ''
            : data.devices.length === 2 
            ? 'two-devices' 
            : data.devices.length > 2 
            ? 'multi-devices' 
            : ''
        }`}
      >
        {devicesToDisplay.map((device, deviceIndex) => {
          // Find AQI parameter 
          const aqiParameter = device.parameters.find(p => 
           p.parameterName.toLowerCase() === 'aqi'
          );
          // Use parameterValue directly as backend returns correct AQI based on AQIStandard
          const overallAQI = aqiParameter?.parameterValue;
          const temperatureParam = device.parameters.find(p => p.parameterName.toLowerCase().includes('temperature'));
          const humidityParam = device.parameters.find(p => p.parameterName.toLowerCase().includes('humidity'));

          const temperature = temperatureParam?.parameterValue ? Math.round(temperatureParam.parameterValue) : null;
          const humidity = humidityParam?.parameterValue ? Math.round(humidityParam.parameterValue) : null;
          // Filter out AQI, Temperature and Humidity from parameters for display in parameter cards
          const filteredParameters =
            device.parameters?.filter(param => {
              const name = param.parameterName.toLowerCase();
              const display = (param.parameterDisplayName || '').toLowerCase();
              return (
                name !== 'aqi' &&
                !name.includes('temperature') &&
                !name.includes('humidity') &&
                !display.includes('temperature') &&
                !display.includes('humidity')
              );
            }) || [];

          return (
            <div 
              key={device.deviceId} 
              className={`device-section zoomOutLevelShareView ${isInitialLoad ? 'animate-slide-in' : ''}`}
              style={isInitialLoad ? { animationDelay: `${deviceIndex * 0.2}s` } : {}}
            >
              {/* Device Header Card */}
              <div 
                className={`device-header-card ${((isMobile && data.devices.length >= 2) || (!isMobile && data.devices.length > 2)) && !isDetailView ? 'clickable-card' : ''}`}
                onClick={((isMobile && data.devices.length >= 2) || (!isMobile && data.devices.length > 2)) && !isDetailView ? () => handleDeviceCardClick(device.deviceId) : undefined}
                style={((isMobile && data.devices.length >= 2) || (!isMobile && data.devices.length > 2)) && !isDetailView ? { cursor: 'pointer' } : {}}
              >
                <div className="device-header-card-content">
                <div className="aqi-gauge">
                  <div className="gauge-circle" style={{ borderColor: HelperFunctions.GetParameterColor(overallAQI ?? 0, 'AQI') }}>
                    <div className="gauge-inner">
                      <p className="aqi-label">
                        {isDetailView 
                          ? getAQILabel(data.AQIStandard)
                          : data.devices.length > 2 && deviceIndex % 2 === 1 
                          ? 'AQI' 
                          : getAQILabel(data.AQIStandard)}
                      </p>
                      <h1 className="aqi-value">{overallAQI ?? '--'}</h1>
                      <span className="aqi-level" style={{ backgroundColor: HelperFunctions.GetParameterColor(overallAQI ?? 0, 'AQI') }}>
                        {getAQILevel(overallAQI ?? undefined)}
                      </span>
                    </div>
                  </div>
                  {getAQILevelDescription(overallAQI ?? undefined) && (
                    <p className="aqi-level-description">
                      <strong>USG</strong> - Unhealthy for Sensitive Groups
                    </p>
                  )}
                </div>

                <div className="device-info">
                  <div className="device-name-row">
                    <div className="device-image-container">
                      <img 
                        src={device.deviceImageUrl || `${process.env.PUBLIC_URL}/DashboardWelcome/DeviceImage.png`} 
                        alt="Device" 
                        className="device-image"
                        onError={(e) => {
                          e.currentTarget.src = `${process.env.PUBLIC_URL}/DashboardWelcome/DeviceImage.png`;
                        }}
                      />
                    </div>
                    <div>
                      <h2 className="share-view-device-name">{device.deviceName}</h2>
                      <p className="device-updated">
                        {isDetailView ? (
                          <>
                            <span className="update-label">Last updated  </span>
                            {new Date(device.lastUpdated).toLocaleString('en-US', {
                              weekday: 'long',
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              timeZone: timezone,
                            })}
                          </>
                        ) : (data.devices.length === 2 && deviceIndex === 1) || (data.devices.length > 2 && deviceIndex % 2 === 1) ? (
                          <>
                            {new Date(device.lastUpdated).toLocaleString('en-US', {
                              weekday: 'long',
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              timeZone: timezone,
                            })}
                            <span className="update-label">— Last updated</span>
                          </>
                        ) : (
                          <>
                            <span className="update-label">Last updated  </span>
                            {new Date(device.lastUpdated).toLocaleString('en-US', {
                              weekday: 'long',
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              timeZone: timezone,
                            })}
                          </>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="quick-stats">
                    {temperature !== undefined && temperature !== null && (
                      <div className="quick-stat">
              <svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M29.8359 25.7334V8.03809C29.8359 5.90625 28.9891 3.86173 27.4816 2.3543C25.9742 0.846867 23.9297 0 21.7979 0C19.666 0 17.6215 0.846867 16.1141 2.3543C14.6066 3.86173 13.7598 5.90625 13.7598 8.03809V25.7334C12.0742 26.986 10.7055 28.616 9.76334 30.4928C8.82113 32.3696 8.3316 34.441 8.33399 36.541C8.33399 43.9072 14.3359 49.9482 21.7012 50H21.7988C24.6291 49.9998 27.3873 49.1078 29.6817 47.4507C31.9761 45.7936 33.69 43.4557 34.5799 40.769C35.4698 38.0824 35.4905 35.1836 34.6391 32.4845C33.7877 29.7853 32.1074 27.4231 29.8369 25.7334H29.8359ZM21.7979 46.2891H21.7275C20.0636 46.2761 18.4306 45.8378 16.9838 45.016C15.5369 44.1942 14.3242 43.016 13.461 41.5935C12.5977 40.171 12.1125 38.5513 12.0515 36.8885C11.9904 35.2256 12.3556 33.5747 13.1123 32.0928C13.7987 30.7628 14.7784 29.6064 15.9775 28.7109L17.4707 27.5986V8.03809C17.4707 6.89045 17.9266 5.78983 18.7381 4.97833C19.5496 4.16683 20.6502 3.71094 21.7979 3.71094C22.9455 3.71094 24.0461 4.16683 24.8576 4.97833C25.6691 5.78983 26.125 6.89045 26.125 8.03809V27.5967L27.6182 28.7109C28.8172 29.6066 29.7967 30.7634 30.4824 32.0938C31.1898 33.4681 31.5563 34.9924 31.5508 36.5381C31.5469 39.1234 30.5181 41.6016 28.6898 43.4295C26.8616 45.2574 24.3831 46.2857 21.7979 46.2891Z" fill="#4C5C68"/>
<path d="M23.5557 29.748V14.6641C23.5557 14.1979 23.3705 13.7508 23.0408 13.4211C22.7112 13.0914 22.2641 12.9062 21.7979 12.9062C21.3317 12.9062 20.8846 13.0914 20.5549 13.4211C20.2253 13.7508 20.0401 14.1979 20.0401 14.6641V29.748C18.3876 30.1758 16.9478 31.1914 15.9904 32.6046C15.033 34.0178 14.6238 35.7316 14.8394 37.4249C15.055 39.1182 15.8806 40.6747 17.1616 41.8029C18.4425 42.9311 20.0909 43.5535 21.7979 43.5535C23.5048 43.5535 25.1532 42.9311 26.4342 41.8029C27.7151 40.6747 28.5407 39.1182 28.7563 37.4249C28.9719 35.7316 28.5627 34.0178 27.6053 32.6046C26.648 31.1914 25.2082 30.1758 23.5557 29.748Z" fill="#4C5C68"/>
<path d="M33.7793 9.76562H40.8418C41.308 9.76562 41.7551 9.58043 42.0848 9.25077C42.4144 8.92112 42.5996 8.47401 42.5996 8.00781C42.5996 7.54161 42.4144 7.09451 42.0848 6.76485C41.7551 6.4352 41.308 6.25 40.8418 6.25H33.7793C33.3131 6.25 32.866 6.4352 32.5363 6.76485C32.2067 7.09451 32.0215 7.54161 32.0215 8.00781C32.0215 8.47401 32.2067 8.92112 32.5363 9.25077C32.866 9.58043 33.3131 9.76562 33.7793 9.76562Z" fill="#4C5C68"/>
<path d="M40.8418 21.3125H33.7793C33.3131 21.3125 32.866 21.4977 32.5363 21.8274C32.2067 22.157 32.0215 22.6041 32.0215 23.0703C32.0215 23.5365 32.2067 23.9836 32.5363 24.3133C32.866 24.6429 33.3131 24.8281 33.7793 24.8281H40.8418C41.308 24.8281 41.7551 24.6429 42.0848 24.3133C42.4144 23.9836 42.5996 23.5365 42.5996 23.0703C42.5996 22.6041 42.4144 22.157 42.0848 21.8274C41.7551 21.4977 41.308 21.3125 40.8418 21.3125Z" fill="#4C5C68"/>
<path d="M33.7793 17.2969H37.3115C37.7777 17.2969 38.2248 17.1117 38.5545 16.782C38.8841 16.4524 39.0693 16.0053 39.0693 15.5391C39.0693 15.0729 38.8841 14.6258 38.5545 14.2961C38.2248 13.9664 37.7777 13.7813 37.3115 13.7812H33.7793C33.5485 13.7812 33.3199 13.8267 33.1066 13.9151C32.8933 14.0034 32.6996 14.1329 32.5363 14.2961C32.3731 14.4593 32.2436 14.6531 32.1553 14.8664C32.067 15.0796 32.0215 15.3082 32.0215 15.5391C32.0215 15.7699 32.067 15.9985 32.1553 16.2117C32.2436 16.425 32.3731 16.6188 32.5363 16.782C32.6996 16.9453 32.8933 17.0747 33.1066 17.1631C33.3199 17.2514 33.5485 17.2969 33.7793 17.2969Z" fill="#4C5C68"/>
</svg>
                        <span className="stat-value">{temperature}°C</span>
                      </div>
                    )}
                    {humidity !== undefined && humidity !== null && (
                      <div className="quick-stat">
                        <svg width="50" height="36" viewBox="0 0 50 36" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M0.818761 11.5683C1.07462 11.6942 7.15166 14.6483 13.2814 14.6483C13.5851 14.6483 13.8879 14.6414 14.1896 14.6278C13.6428 15.6785 13.1896 16.66 12.8322 17.5711C12.8312 17.5721 12.8312 17.5721 12.8312 17.573C12.9816 17.576 13.131 17.5779 13.2814 17.5779C19.9551 17.5779 26.2138 14.5594 26.955 14.1893C27.0722 14.1317 30.4872 12.4872 34.283 11.914C34.2703 11.8925 34.2576 11.871 34.244 11.8495C33.741 11.0155 33.1893 10.1464 32.5907 9.24311C33.9119 8.97163 35.3201 8.78901 36.7185 8.78901C42.154 8.78901 47.8326 11.5409 47.8882 11.5683C48.6138 11.9247 49.4917 11.6259 49.8492 10.9003C50.2066 10.1747 49.9068 9.29682 49.1812 8.9394C48.9253 8.81343 42.8483 5.85938 36.7185 5.85938C34.658 5.85938 32.6151 6.19335 30.7997 6.64159C27.2089 7.52634 24.5127 8.85932 24.3457 8.94331C24.2988 8.96577 20.4024 10.8857 15.9572 11.5185C15.0783 11.6435 14.1779 11.7186 13.2814 11.7186C10.5637 11.7186 7.78544 11.0312 5.67903 10.3398C3.57262 9.64837 2.13905 8.95307 2.11073 8.9394C1.38516 8.58296 0.507243 8.88178 0.150804 9.60736C-0.206611 10.3329 0.0931882 11.2108 0.818761 11.5683Z" fill="#4C5C68"/>
<path d="M49.1812 17.7285C48.9253 17.6025 42.8483 14.6484 36.7185 14.6484C36.4226 14.6484 36.1267 14.6553 35.8318 14.668C30.0194 14.9277 24.5849 17.6123 24.3457 17.7324C24.29 17.7597 18.711 20.5077 13.2814 20.5077C12.8439 20.5077 12.4055 20.4901 11.968 20.4569C9.68677 20.286 7.44657 19.7089 5.67903 19.1288C3.57262 18.4374 2.13905 17.7421 2.11073 17.7285C1.38516 17.372 0.507243 17.6708 0.150804 18.3964C-0.206611 19.122 0.0931882 19.9999 0.818761 20.3573C1.05509 20.4735 6.26495 23.0067 11.8928 23.3885C12.0022 24.4149 12.2307 25.4061 12.5637 26.3484C12.5637 26.3504 12.5646 26.3523 12.5656 26.3553C12.7307 26.3611 12.8957 26.3641 13.0617 26.365C13.135 26.366 13.2082 26.367 13.2814 26.367C19.9551 26.367 26.2138 23.3485 26.955 22.9784C27.0976 22.909 32.0829 20.5077 36.7185 20.5077C36.8376 20.5077 36.9558 20.511 37.0747 20.5131C37.0836 20.5133 37.0924 20.5135 37.1013 20.5136C37.4177 20.5224 37.7351 20.5409 38.0525 20.5692C38.0515 20.5624 38.0496 20.5546 38.0476 20.5468C37.8923 19.661 37.6003 18.6728 37.1736 17.5849C42.4665 17.7294 47.8345 20.331 47.8882 20.3573C48.6138 20.7138 49.4917 20.4149 49.8492 19.6894C50.2066 18.9638 49.9068 18.0859 49.1812 17.7285Z" fill="#4C5C68"/>
<path d="M49.1812 26.5175C48.9449 26.4013 43.7291 23.8652 38.0974 23.4853C37.6394 23.4541 37.1795 23.4375 36.7185 23.4375C30.5839 23.4375 24.5967 26.3955 24.3457 26.5214C24.293 26.5478 19.213 29.0497 14.0314 29.2802C13.7814 29.2909 13.5314 29.2968 13.2814 29.2968C10.5637 29.2968 7.78544 28.6093 5.67903 27.9179C3.57262 27.2265 2.13905 26.5312 2.11073 26.5175C1.38516 26.1611 0.507243 26.4599 0.150804 27.1855C-0.206611 27.9111 0.0931882 28.789 0.818761 29.1464C1.07462 29.2724 7.15166 32.2264 13.2814 32.2264C14.3478 32.2264 15.4083 32.1375 16.4367 31.9891C17.5333 32.9276 18.7862 33.6903 20.1495 34.2313H20.1505C21.6524 34.8279 23.2881 35.156 25 35.156C29.5517 35.156 33.5721 32.8377 35.9431 29.3192C36.5525 28.413 37.0525 27.4286 37.4246 26.3828C42.6344 26.5996 47.8345 29.12 47.8882 29.1464C48.6138 29.5028 49.4917 29.204 49.8492 28.4784C50.2066 27.7529 49.9068 26.8749 49.1812 26.5175Z" fill="#4C5C68"/>
<path d="M28.9354 4.09954C27.3592 2.00973 26.2001 0.617177 26.1239 0.526358C25.8456 0.192379 25.4344 0 24.9999 0C24.5653 0 24.1542 0.192379 23.8759 0.526358C23.7577 0.666981 21.041 3.92864 18.2012 8.07017C20.8886 7.35729 22.954 6.36219 23.0448 6.31825C23.4765 6.10341 25.7743 4.99112 28.9354 4.09954Z" fill="#4C5C68"/>
</svg>

                        <span className="stat-value">{humidity}%</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right arrow button for multi-device view */}
                {/* On mobile: show for 2+ devices, on desktop: show for >2 devices */}
                {((isMobile && data.devices.length >= 2) || (!isMobile && data.devices.length > 2)) && !isDetailView && (
                  <button 
                    className="device-card-right-arrow-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeviceCardClick(device.deviceId);
                    }}
                    aria-label="View device details"
                  >
                    <ArrowRight size={24} />
                  </button>
                )}
               
                </div>
              </div>

              {/* Parameters Grid - Hidden for multi-device view on mobile, shown for detail view or desktop 2-device view */}
              {((isMobile && (data.devices.length < 2 || isDetailView)) || (!isMobile && (data.devices.length <= 2 || isDetailView))) && (
              <div 
                className={`parameters-grid ${
                  data.devices.length === 2 
                    ? 'two-devices-grid' 
                    : ''
                }`}
                // style={{
                //   gridTemplateColumns: data.devices.length === 2 
                //     ? `repeat(2, 1fr)`
                //     : `repeat(${Math.ceil(filteredParameters.length / 2)}, 1fr)`
                // }}
              >
                {filteredParameters.length > 0 ? (
                  filteredParameters.map((param, index) => (
                    <div 
                      key={`${device.deviceId}-${param.parameterDriverId}-${index}`} 
                      className={`parameter-card ${isInitialLoad ? 'animate-fade-in' : ''}`}
                      style={isInitialLoad ? { animationDelay: `${index * 0.1}s` } : {}}
                    >
                      <div className="parameter-header">
                        <h3>{param.parameterDisplayName}</h3>
                        {/* <h4>{param.parameterName}</h4> */}
                        {/* Show parameterName only for NON-special parameters */}
                          {!["Temperature", "Humidity", "Virus Index", "Mold Risk"]
                            .includes(param.parameterDisplayName) &&
                            (data.devices.length === 1 || isDetailView) && (
                              <p className="parameter-label">{param.parameterName}</p>
                          )}
                      </div>
                      <div className="parameter-value-container">
                        <div className="parameter-value">
                        {param.parameterValue == null
                      ? '--'
                      : param.parameterName.toLowerCase().includes('temperature') || param.parameterName.toLowerCase().includes('humidity')
                        ? Math.round(param.parameterValue)
                        : Number.isInteger(param.parameterValue)
                          ? param.parameterValue
                          : param.parameterValue < 0.01 ? param.parameterValue.toFixed(4) : param.parameterValue.toFixed(2)}
                          <span className="parameter-unit">{param.parameterUnit}</span>
                        </div>
                        <div 
                          className="parameter-indicator" 
                          style={{ 
                            background: GetParameterColor(param.parameterValue, param.subIndexUS, param.parameterName)
                          }}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-parameters">No parameters available</div>
                )}
              </div>
              )}

            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PublicShareView;

